import { useMutation } from "@apollo/client/react"
import { Loader2 } from "lucide-react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { useUiAccess } from "~/auth/use-ui-access"
import { useViewerMaybe } from "~/auth/use-viewer"
import { useNavigateAfterAuth } from "~/auth/utils"
import { rootPath } from "~/common/paths"
import { Button } from "~/ui/button"
import { updateCsrfTag } from "./utils"

export const EMAIL_TOKEN_AUTH_MUTATION = gql(/* GraphQL */ `
  mutation EmailTokenAuth($input: EmailTokenUserAuthInput!) {
    emailTokenUserAuth(input: $input) {
      success
      csrfToken
    }
  }
`)

const checkValidity = (clientAuthCode: string | undefined) => {
  const storedAuthCodes = JSON.parse(localStorage.getItem("authCodes") || "[]")
  return storedAuthCodes.includes(clientAuthCode)
}

export const EmailAuthScreen: React.FC = () => {
  const { email, token, clientAuthCode } = useParams<{
    email: string
    token: string
    clientAuthCode: string
  }>()
  const [emailTokenAuth] = useMutation(EMAIL_TOKEN_AUTH_MUTATION)
  const navigate = useNavigate()
  const [isValidClient] = useState<boolean | null>(checkValidity(clientAuthCode))
  const [authState, setAuthState] = useState<"idle" | "running" | "failed">(() =>
    checkValidity(clientAuthCode) ? "running" : "idle"
  )
  const { navigateAfterAuth } = useNavigateAfterAuth()
  const {
    result: { refetch: viewerRefetch },
  } = useViewerMaybe()
  const { refetch: refetchUiAccess } = useUiAccess()

  const handleAuthentication = useCallback(async () => {
    if (!email || !token || !clientAuthCode) {
      toast.error("Couldn't sign you in", {
        description: "The link is missing some information. Send yourself a fresh one.",
      })
      setAuthState("failed")
      return
    }

    const result = await emailTokenAuth({
      variables: {
        input: {
          email,
          token,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    })

    if (result.error) {
      console.error("Email token auth failed:", result.error)
      toast.error("Couldn't sign you in", {
        description: "Something went wrong. Try the link again in a minute.",
      })
      setAuthState("failed")
      return
    }

    if (result.data?.emailTokenUserAuth.success) {
      updateCsrfTag(result.data.emailTokenUserAuth.csrfToken)
      await viewerRefetch()
      await refetchUiAccess()
      toast.success("You're signed in.")
      navigateAfterAuth()
    } else {
      toast.error("Couldn't sign you in", {
        description: "The link may have expired. Send yourself a fresh one.",
      })
      setAuthState("failed")
    }
  }, [
    email,
    token,
    clientAuthCode,
    emailTokenAuth,
    viewerRefetch,
    refetchUiAccess,
    navigateAfterAuth,
  ])

  // Auto-fire the auth mutation as soon as we know this is the right device.
  // Email scanners that pre-fetch the link won't have the localStorage entry,
  // so they hit the cross-device branch and don't burn the token.
  const autoFiredRef = useRef(false)
  useEffect(() => {
    if (autoFiredRef.current) return
    if (isValidClient !== true) return
    autoFiredRef.current = true
    queueMicrotask(() => {
      handleAuthentication()
    })
  }, [isValidClient, handleAuthentication])

  if (isValidClient === null) {
    return <div>Loading...</div>
  }

  return (
    <div className="w-full space-y-6">
      <h1 className="text-center font-display text-3xl font-semibold">Welcome back</h1>
      {isValidClient ? (
        authState === "failed" ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-stone-700">
              We couldn&apos;t finish signing you in. The link may have expired.
            </p>
            <Button
              onClick={() => {
                setAuthState("running")
                handleAuthentication()
              }}
              className="w-full"
            >
              Try again
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-stone-500" aria-hidden />
            <p className="text-sm text-stone-700">Signing you in…</p>
          </div>
        )
      ) : (
        <div className="space-y-4 text-center">
          <p className="text-sm text-stone-700">
            Looks like you opened the link on a different device or browser than the one you started
            from. Open this link on the original device, or send yourself a fresh one.
          </p>
          <Button onClick={() => navigate(rootPath({}))} className="w-full">
            Back to home
          </Button>
        </div>
      )}
    </div>
  )
}
